import { useState } from 'react';
import { PlanGate } from '@/shared/components/PlanGate';
import { usePostCare, PostCareTemplate, SERVICE_TYPES, ServiceType } from '@/features/postcare/hooks/usePostCare';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Search, Plus, Pencil, Trash2, Heart, Share2, Copy, Mail, Link2, Star } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { motion } from 'framer-motion';

const SERVICE_LABELS: Record<string, string> = {
  tattoo: 'Tatouage',
  piercing: 'Piercing',
  microblading: 'Microblading',
  dermographie: 'Dermographie',
  laser: 'Laser',
  autre: 'Autre',
};

const SERVICE_EMOJIS: Record<string, string> = {
  tattoo: '🖋️',
  piercing: '💎',
  microblading: '✨',
  dermographie: '🎨',
  laser: '⚡',
  autre: '📋',
};

type FormData = { title: string; content: string; service_type: string; is_default: boolean };
const emptyForm: FormData = { title: '', content: '', service_type: 'tattoo', is_default: false };

export default function PostCarePage() {
  const { templates, isLoading, createTemplate, updateTemplate, deleteTemplate } = usePostCare();
  const { toast } = useToast();

  const [search, setSearch] = useState('');
  const [filterService, setFilterService] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PostCareTemplate | null>(null);
  const [sharingTemplate, setSharingTemplate] = useState<PostCareTemplate | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);

  const filtered = templates
    .filter(t => filterService === 'all' || t.service_type === filterService)
    .filter(t => [t.title, t.content].some(v => v.toLowerCase().includes(search.toLowerCase())));

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (t: PostCareTemplate) => {
    setEditing(t);
    setForm({ title: t.title, content: t.content, service_type: t.service_type, is_default: t.is_default });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    try {
      if (editing) {
        await updateTemplate.mutateAsync({ id: editing.id, ...form });
      } else {
        await createTemplate.mutateAsync(form);
      }
      setDialogOpen(false);
      toast({ title: 'Enregistré', description: '✓' });
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTemplate.mutateAsync(id);
      toast({ title: 'Supprimé', description: '✓' });
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleShare = (t: PostCareTemplate) => {
    setSharingTemplate(t);
    setShareDialogOpen(true);
  };

  const copyToClipboard = async () => {
    if (!sharingTemplate) return;
    const text = `${sharingTemplate.title}\n\n${sharingTemplate.content}`;
    await navigator.clipboard.writeText(text);
    toast({ title: 'Copié', description: 'Le contenu a été copié dans le presse-papiers' });
  };

  const shareViaEmail = () => {
    if (!sharingTemplate) return;
    const subject = encodeURIComponent(`Soins post-acte : ${sharingTemplate.title}`);
    const body = encodeURIComponent(sharingTemplate.content);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareViaLink = async () => {
    if (!sharingTemplate) return;
    const text = `${sharingTemplate.title}\n\n${sharingTemplate.content}`;
    if (navigator.share) {
      await navigator.share({ title: sharingTemplate.title, text });
    } else {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Lien copié', description: 'Partagez-le avec votre client' });
    }
  };

  return (
    <PlanGate
      title={{ fr: 'Soins post-acte', en: 'Post-care', es: 'Cuidados post-acto' }}
      description={{
        fr: 'Créez et partagez vos fiches de soins post-séance avec vos clients. Disponible dès le plan Solo.',
        en: 'Create and share post-session care sheets with your clients. Available from the Solo plan.',
        es: 'Crea y comparte fichas de cuidados post-sesión con tus clientes. Disponible desde el plan Solo.',
      }}
    >
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-display text-foreground">Soins post-acte</h1>
          <p className="text-muted-foreground mt-1">Templates de soins à partager avec vos clients</p>
        </div>
        <Button onClick={openNew} className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground">
          <Plus size={16} /> Nouveau template
        </Button>
      </motion.div>

      {/* Filters */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-secondary/50 border-border/50 h-11" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Button variant={filterService === 'all' ? 'default' : 'secondary'} size="sm" onClick={() => setFilterService('all')} className="rounded-full text-xs h-9">
            Tous
          </Button>
          {SERVICE_TYPES.map(st => (
            <Button key={st} variant={filterService === st ? 'default' : 'secondary'} size="sm" onClick={() => setFilterService(st)} className="rounded-full text-xs h-9">
              {SERVICE_EMOJIS[st]} {SERVICE_LABELS[st]}
            </Button>
          ))}
        </div>
      </motion.div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-52 rounded-2xl bg-secondary/50" />)}
        </div>
      ) : filtered.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20 text-muted-foreground">
          <Heart size={56} className="mx-auto mb-4 opacity-20" />
          <p className="text-lg">Aucun template de soins trouvé</p>
          <Button onClick={openNew} variant="secondary" className="mt-4 gap-2"><Plus size={16} /> Créer un template</Button>
        </motion.div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((tmpl, i) => (
            <motion.div
              key={tmpl.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className={`glass-card-hover p-5 space-y-3 group relative ${tmpl.is_default ? 'ring-1 ring-primary/30' : ''}`}
            >
              {tmpl.is_default && (
                <div className="absolute top-3 right-3">
                  <Star size={14} className="text-primary fill-primary" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-lg">{SERVICE_EMOJIS[tmpl.service_type] || '📋'}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                  {SERVICE_LABELS[tmpl.service_type] || tmpl.service_type}
                </span>
              </div>
              <h3 className="font-semibold text-foreground text-base">{tmpl.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-4 whitespace-pre-wrap">{tmpl.content}</p>
              <div className="flex items-center justify-between pt-1">
                <Button variant="ghost" size="sm" className="gap-1.5 text-xs text-primary hover:text-primary" onClick={() => handleShare(tmpl)}>
                  <Share2 size={13} /> Partager
                </Button>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => openEdit(tmpl)}>
                    <Pencil size={13} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(tmpl.id)}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg glass-card border-border/50">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? 'Modifier le template' : 'Nouveau template de soins'}</DialogTitle>
            <DialogDescription className="sr-only">Formulaire de template de soins post-acte</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground text-xs">Titre *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="bg-secondary/50 border-border/50" placeholder="Ex: Soins après tatouage" />
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Type de service</Label>
              <Select value={form.service_type} onValueChange={v => setForm(f => ({ ...f, service_type: v }))}>
                <SelectTrigger className="bg-secondary/50 border-border/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map(st => (
                    <SelectItem key={st} value={st}>{SERVICE_EMOJIS[st]} {SERVICE_LABELS[st]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Instructions de soins</Label>
              <Textarea
                value={form.content}
                onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                className="bg-secondary/50 border-border/50 min-h-[220px] resize-y"
                placeholder="Décrivez les étapes de soins post-acte...&#10;&#10;Exemple :&#10;1. Ne pas toucher la zone traitée pendant 2h&#10;2. Appliquer la crème cicatrisante 2x/jour&#10;3. Éviter l'exposition au soleil pendant 2 semaines"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_default} onCheckedChange={v => setForm(f => ({ ...f, is_default: v }))} />
              <Label className="text-sm text-muted-foreground">Template par défaut pour ce service</Label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border/50">Annuler</Button>
            <Button onClick={handleSave} disabled={!form.title.trim()} className="bg-primary hover:bg-primary/90 text-primary-foreground">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-sm glass-card border-border/50">
          <DialogHeader>
            <DialogTitle className="font-display">Partager les soins</DialogTitle>
            <DialogDescription className="sr-only">Partager les instructions de soins avec le client</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-2">{sharingTemplate?.title}</p>
          <div className="space-y-2">
            <Button variant="secondary" className="w-full justify-start gap-3 h-12" onClick={shareViaEmail}>
              <Mail size={18} className="text-primary" /> Envoyer par email
            </Button>
            <Button variant="secondary" className="w-full justify-start gap-3 h-12" onClick={shareViaLink}>
              <Link2 size={18} className="text-primary" /> Partager via un lien
            </Button>
            <Button variant="secondary" className="w-full justify-start gap-3 h-12" onClick={copyToClipboard}>
              <Copy size={18} className="text-primary" /> Copier le contenu
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </PlanGate>
  );
}
